import { useCallback, useMemo, useState } from "react"
import { useLocation } from "react-router"

import { IconMessage, IconX } from "@tabler/icons-react"

import { WarningCycleIcon } from "@/components/common/icons/WarningCycleIcon/WarningCycleIcon"
import { LogoMark } from "@/components/common/Logo/LogoMark"
import { Typography } from "@/components/common/Typography/Typography"
import { AnswerSourceGroup } from "@/components/feature/AnswerSourceGroup/AnswerSourceGroup"
import { TextAreaListItem } from "@/components/feature/CollapsibleQuestionnaireList/TextAreaListItem/TextAreaListItem"
import { AskForHelpModal } from "@/components/feature/Modals/AskForHelpModal/AskForHelpModal"
import { Button as CnButton } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Typography as ShadcnTypography } from "@/components/ui/typography"
import { useAgentChatContext } from "@/contexts/AgentChatContext"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useAgentChatEnabled } from "@/features/agent-chat/useAgentChatEnabled"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AnswerMemorySuggestion } from "@/pages/questionnaire/AnswerMemorySuggestion"
import { QuestionAnswerCard } from "@/pages/questionnaire/QuestionAnswerCard"
import {
  getQuestionAnswerState,
  getQuestionAnswerTone,
  hasQuestionAnswerResponse,
  needsQuestionAnswerHelp,
} from "@/pages/questionnaire/questionAnswerState"
import { useLayoutStore } from "@/store/layoutStore"
import { EditResponsePairKey } from "@/types/hooks/useTextAreaStates"
import { QuestionnaireResponsePair } from "@/types/questionnaire/models"
import { classNames } from "@/utils/classNames"

import { normalizeOptionText } from "@/utils/questionnaire/optionText"
import { OptionClearSelectionButton } from "./OptionClearSelectionButton"
import {
  isOptionSelected,
  isOptionSelectedInList,
  toggleOptionInValue,
} from "./utils"

interface AnswerItemProps {
  responsePair: QuestionnaireResponsePair
  value: string
  onChange: (nextValue: string) => void
  selectedQuestionId: string | undefined
  onInputFocus?: (questionId: string, optionBoxId?: string) => void
  onDelete?: (responsePairId: string) => void
  showContext?: boolean
  questionnaireId: string
}

const EDIT_RESPONSE_PAIR_KEY: EditResponsePairKey = "response"

export const PdfAnswerItem: React.FC<AnswerItemProps> = ({
  responsePair,
  value,
  onChange,
  selectedQuestionId,
  onInputFocus,
  onDelete,
  showContext,
  questionnaireId,
}) => {
  const { translate } = useTranslation()
  const { setTargetedAnswer, targetedAnswer } = useAgentChatContext()
  const isTargeted =
    targetedAnswer?.questionnaireId === questionnaireId &&
    targetedAnswer?.responsePairId === responsePair.id
  const { openChatPanel } = useLayoutStore()
  const sidechatEnabled = useFeatureFlag("ENABLE_SIDECHAT")
  const agentChatEnabled = useAgentChatEnabled()
  // Both gates are required: ENABLE_SIDECHAT controls whether the docked panel
  // exists at all, and agentChatEnabled (RAG_V2_CHAT+AGENT_TOOLS) controls
  // whether that panel actually wires up the targeted-answer context once
  // opened — without it, clicking this would open the panel but the target
  // would silently never reach the chat request.
  const askAgentEnabled = sidechatEnabled && agentChatEnabled

  const handleAskAgent = useCallback(() => {
    setTargetedAnswer({
      questionnaireId,
      responsePairId: responsePair.id,
      questionLabel: responsePair.question,
    })
    openChatPanel()
  }, [
    setTargetedAnswer,
    openChatPanel,
    questionnaireId,
    responsePair.id,
    responsePair.question,
  ])
  const query = new URLSearchParams(useLocation().search)
  const urlQueryId = query.get("question")
  const [isAskForHelpModalOpen, setIsAskForHelpModalOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const handleOpenShareModal = () => {
    setIsAskForHelpModalOpen(true)
  }

  const onTextChange = useCallback(
    (changedText: string) => {
      onChange(changedText)
    },
    [onChange],
  )

  const handleRadioSelect = useCallback(
    (nextValue: string, optionId: string) => {
      onInputFocus?.(responsePair.id, optionId)
      onChange(nextValue)
    },
    [onChange, onInputFocus, responsePair.id],
  )

  const validAnswerOptions = useMemo(
    () =>
      (responsePair.pdfPlacement?.extraction?.optionsWithBbox || [])
        .map((o) => o?.text)
        .filter((t): t is string => !!t),
    [responsePair.pdfPlacement?.extraction?.optionsWithBbox],
  )

  const handleCheckboxToggle = useCallback(
    (optionLabel: string, optionId: string) => {
      onInputFocus?.(responsePair.id, optionId)
      onChange(toggleOptionInValue(value, optionLabel, validAnswerOptions))
    },
    [onChange, onInputFocus, responsePair.id, validAnswerOptions, value],
  )

  const questionType = responsePair.type || "free-text"
  const isManual = responsePair.creationSource === "manual"
  const isManualBooleanMark = isManual && questionType === "boolean"
  const isManualFreeTextOrCheckbox =
    isManual &&
    (questionType === "free-text" || questionType === "multiple-select")
  const answerState = getQuestionAnswerState(responsePair)
  const answerTone = getQuestionAnswerTone(responsePair)
  const hasResponse = hasQuestionAnswerResponse(responsePair)

  const handleHighlightCell = useCallback(() => {
    const hasOptionPlacement =
      (responsePair.pdfPlacement?.userPlacement?.optionsPlacements?.length ??
        0) > 0 ||
      (responsePair.pdfPlacement?.extraction?.optionsWithBbox?.length ?? 0) > 0

    const targetBoxId =
      questionType === "free-text" || !hasOptionPlacement
        ? responsePair.id
        : `${responsePair.id},radio,0`

    onInputFocus?.(responsePair.id, targetBoxId)
  }, [onInputFocus, questionType, responsePair])

  const supportsClearSelection =
    !isManualBooleanMark && !isManualFreeTextOrCheckbox
  const showHeaderClearSelection = supportsClearSelection

  const isSelectionEmpty = useCallback(() => {
    return !value || value.trim().length === 0
  }, [value])

  const handleClearSelection = useCallback(() => {
    // Clear selection and update placement via the existing onChange handler chain.
    onInputFocus?.(responsePair.id)
    onChange("")
  }, [onChange, onInputFocus, responsePair.id])

  const handleClearRadioOption = useCallback(
    (optionId: string, isChecked: boolean) => {
      if (!isChecked) {
        return
      }

      onInputFocus?.(responsePair.id, optionId)
      onChange("")
    },
    [onChange, onInputFocus, responsePair.id],
  )

  const handleClearCheckboxOption = useCallback(
    (optionLabel: string, optionId: string, isChecked: boolean) => {
      if (!isChecked) {
        return
      }

      onInputFocus?.(responsePair.id, optionId)
      onChange(toggleOptionInValue(value, optionLabel, validAnswerOptions))
    },
    [onChange, onInputFocus, responsePair.id, validAnswerOptions, value],
  )

  const renderInput = () => {
    if (isManualBooleanMark) {
      return null
    }

    const optionsWithBbox =
      responsePair.pdfPlacement?.extraction?.optionsWithBbox || []

    switch (questionType) {
      case "boolean":
      case "multiple-choice": {
        if (!optionsWithBbox.length) {
          return (
            <TextAreaListItem
              text={value}
              responsePair={responsePair}
              onTextChange={onTextChange}
              editResponsePairKey={EDIT_RESPONSE_PAIR_KEY}
              onFocus={handleHighlightCell}
            />
          )
        }

        return (
          <div className="flex flex-col gap-XS">
            {optionsWithBbox.map((opt, idx) => {
              const label = normalizeOptionText(opt.text || "")
              // Use case-insensitive comparison for radio button selection
              const checked = isOptionSelected(value, label)
              const optionId = `${responsePair.id},radio,${idx}`

              return (
                <div
                  key={optionId}
                  className="flex items-center justify-between px-2"
                >
                  <label
                    htmlFor={optionId}
                    className={classNames(
                      "flex flex-1 cursor-pointer items-center gap-S rounded-sm py-2",
                    )}
                    onMouseDown={() => {
                      handleRadioSelect?.(label, optionId)
                    }}
                  >
                    <input
                      id={optionId}
                      name={responsePair.id}
                      type="radio"
                      checked={checked}
                      onChange={() => {
                        handleRadioSelect(label, optionId)
                      }}
                    />

                    <Typography variant="textS">{label}</Typography>
                  </label>

                  {checked && (
                    <OptionClearSelectionButton
                      onClear={() => {
                        handleClearRadioOption(optionId, checked)
                      }}
                    />
                  )}
                </div>
              )
            })}
          </div>
        )
      }

      case "multiple-select": {
        if (!optionsWithBbox.length) {
          return null
        }

        return (
          <div className="flex flex-col gap-XS">
            {optionsWithBbox.map((opt, idx) => {
              const label = normalizeOptionText(opt.text || "")
              const checked = isOptionSelectedInList(value, label)

              const optionId = `${responsePair.id},radio,${idx}`
              return (
                <div
                  key={optionId}
                  className="flex flex-row items-center justify-between gap-2 px-2 py-2"
                >
                  <div className="flex flex-row items-center gap-2">
                    <Checkbox
                      id={optionId}
                      checked={checked}
                      onCheckedChange={() => {
                        handleCheckboxToggle(label, optionId)
                      }}
                    />
                    <Label htmlFor={optionId}>{label}</Label>
                  </div>

                  {checked && !isManual && (
                    <OptionClearSelectionButton
                      onClear={() => {
                        handleClearCheckboxOption(label, optionId, checked)
                      }}
                    />
                  )}
                </div>
              )
            })}
          </div>
        )
      }

      case "free-text":
      default:
        return (
          <TextAreaListItem
            text={value}
            responsePair={responsePair}
            onTextChange={onTextChange}
            editResponsePairKey={EDIT_RESPONSE_PAIR_KEY}
            onFocus={handleHighlightCell}
          />
        )
    }
  }

  const handleConfirmDelete = () => {
    onDelete?.(responsePair.id)
    setIsDeleteDialogOpen(false)
  }

  const cardTone = isManualBooleanMark ? "neutral" : answerTone

  const statusMessage = !isManualBooleanMark ? (
    !hasResponse ? (
      <div className="flex items-center gap-XS">
        <WarningCycleIcon className="text-redLight" />
        <Typography variant="textXS" className="text-redLight">
          {translate("QUESTIONNAIRE_ANSWERS/WARNING_NO_GENERATED_ANSWER")}
        </Typography>
      </div>
    ) : answerState === "low-confidence" ? (
      <div className="flex items-center gap-XS">
        <WarningCycleIcon className="text-warning" />
        <Typography variant="textXS" className="text-warning">
          {translate("QUESTIONNAIRE_ANSWERS/WARNING_LOW_CONFIDENCE_ANSWER")}
        </Typography>
      </div>
    ) : null
  ) : null

  const askAgentAction = askAgentEnabled ? (
    <button
      type="button"
      onClick={handleAskAgent}
      className={classNames(
        "inline-flex items-center justify-center border-0 bg-transparent p-0 hover:cursor-pointer",
      )}
      title={translate("QUESTIONNAIRE_ANSWERS/ASK_AGENT")}
      aria-label={translate("QUESTIONNAIRE_ANSWERS/ASK_AGENT")}
    >
      <LogoMark
        className={classNames(
          "h-4 w-4",
          isTargeted ? "text-primary" : "text-grey5 hover:text-white",
        )}
      />
    </button>
  ) : null

  const askForHelpAction =
    !isManualBooleanMark && needsQuestionAnswerHelp(responsePair) ? (
      <div
        onClick={handleOpenShareModal}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleOpenShareModal()
          }
        }}
        className={classNames("hover:cursor-pointer")}
        role="button"
        tabIndex={0}
      >
        <IconMessage className="text-grey5 hover:text-white" stroke={1} />
      </div>
    ) : null

  const statusAction =
    askAgentAction || askForHelpAction ? (
      <div className="flex items-center gap-XS">
        {askAgentAction}
        {askForHelpAction}
      </div>
    ) : null

  // FREE TEXT ONLY, deliberately: a boolean or multiple-choice answer is written as one of
  // the question's allowed values, and a remembered sentence is not one of them.
  const memorySuggestion =
    responsePair.memory &&
    !isManualBooleanMark &&
    (responsePair.type || "free-text") === "free-text" ? (
      // PURELY ADVISORY: it reads the remembered answer out and offers a rating. It has no
      // write path at all, so there is nothing to hand it — the reviewer types whatever
      // they decide, through the ordinary change this card already owns.
      <AnswerMemorySuggestion
        responsePairId={responsePair.id}
        questionnaireId={responsePair.questionnaire}
        suggestion={responsePair.memory}
      />
    ) : null

  const footer =
    memorySuggestion || (!isManualBooleanMark && hasResponse) ? (
      <div className="flex flex-col gap-2">
        {memorySuggestion}
        {!isManualBooleanMark && hasResponse ? (
          <AnswerSourceGroup citations={responsePair.citations} />
        ) : null}
      </div>
    ) : null

  return (
    <>
      {isAskForHelpModalOpen && (
        <AskForHelpModal
          responsePairId={responsePair.id}
          setIsAskForHelpModalOpen={setIsAskForHelpModalOpen}
        />
      )}

      <div
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsFocused(false)
          }
        }}
      >
        <QuestionAnswerCard
          id={`question-${responsePair.id}`}
          tone={cardTone}
          isActive={isFocused || selectedQuestionId === responsePair.id}
          className={classNames(
            urlQueryId === responsePair.id ? "animate-highlight" : "",
          )}
          header={
            <button
              type="button"
              onClick={handleHighlightCell}
              className="flex w-full text-left"
            >
              <ShadcnTypography
                as="small"
                className="font-medium leading-snug text-white/80"
              >
                {responsePair.question}
                {showContext && responsePair.context && (
                  <span className="ml-2 text-xs text-white/40">
                    ({responsePair.context})
                  </span>
                )}
              </ShadcnTypography>
            </button>
          }
          headerAction={
            (showHeaderClearSelection || (isManual && onDelete)) && (
              <TooltipProvider>
                <div className="flex items-center gap-1">
                  {showHeaderClearSelection && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex" tabIndex={0}>
                          <OptionClearSelectionButton
                            ariaLabel={translate(
                              "QUESTIONNAIRE_ANSWERS/CLEAR_SELECTION",
                            )}
                            disabled={isSelectionEmpty()}
                            onClear={handleClearSelection}
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {translate("QUESTIONNAIRE_ANSWERS/CLEAR_SELECTION")}
                      </TooltipContent>
                    </Tooltip>
                  )}

                  {isManual && onDelete && (
                    <>
                      <button
                        type="button"
                        title={translate("QUESTIONNAIRE/DELETE")}
                        onClick={(e) => {
                          e.stopPropagation()
                          setIsDeleteDialogOpen(true)
                        }}
                        className="rounded p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                      >
                        <IconX size={16} stroke={1} aria-hidden="true" />
                      </button>

                      <Dialog
                        open={isDeleteDialogOpen}
                        onOpenChange={setIsDeleteDialogOpen}
                      >
                        <DialogContent className="sm:max-w-[425px]">
                          <DialogHeader>
                            <DialogTitle>
                              {translate(
                                "QUESTIONNAIRE_ANSWERS/DELETE_CUSTOM_FIELD_TITLE",
                              )}
                            </DialogTitle>
                            <DialogDescription>
                              {translate(
                                "QUESTIONNAIRE_ANSWERS/DELETE_CUSTOM_FIELD_DESCRIPTION",
                              )}
                            </DialogDescription>
                          </DialogHeader>

                          <DialogFooter className="gap-2 sm:gap-0">
                            <CnButton
                              type="button"
                              variant="outline"
                              onClick={() => setIsDeleteDialogOpen(false)}
                            >
                              {translate(
                                "QUESTIONNAIRE_ANSWERS/DELETE_CUSTOM_FIELD_CANCEL",
                              )}
                            </CnButton>
                            <CnButton
                              type="button"
                              onClick={handleConfirmDelete}
                            >
                              {translate(
                                "QUESTIONNAIRE_ANSWERS/DELETE_CUSTOM_FIELD_CONFIRM",
                              )}
                            </CnButton>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </>
                  )}
                </div>
              </TooltipProvider>
            )
          }
          body={renderInput()}
          statusMessage={statusMessage}
          statusAction={statusAction}
          footer={footer}
        />
      </div>
    </>
  )
}
